import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import { PaymentSettingType } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { paymentSettingActions } from '@nxsys/store/slices/paymentSetting/reducer/paymentSettingSlice';
import {
  GetPaymentRateListParams,
  PaymentRate,
} from '@nxsys/store/slices/paymentSetting/types';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreatePaymentRateModal } from '../create';
import { ViewPaymentRateModal } from '../detail';
import { getColumns } from './columns';

const PaymentRateListing = () => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const dispatch = useDispatch<AppDispatch>();

  const { paymentRateList } = useSelector(
    (state: AppState) => state.paymentSettingStore,
  );
  const { loading, list, pagination, queryParams } = paymentRateList;

  const [searchValue, setSearchValue] = useState('');

  const [openViewPaymentRate, setOpenViewPaymentRate] = useState<{
    open: boolean;
    data: PaymentRate | null;
  }>({
    open: false,
    data: null,
  });

  const [openCreatePaymentRate, setOpenCreatePaymentRate] = useState<{
    open: boolean;
  }>({
    open: false,
  });

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetPaymentRateListParams) => {
    const newParams = {
      ...params,
      includePaymentModels:
        isBureau
          ? [PAYMENT_MODEL_ENUM.PAYEE]
          : [
              PAYMENT_MODEL_ENUM.CIS,
              PAYMENT_MODEL_ENUM.NON_CIS,
              PAYMENT_MODEL_ENUM.PAYEE,
              PAYMENT_MODEL_ENUM.HYBRID,
              PAYMENT_MODEL_ENUM.PAYE_HYBRID,
              PAYMENT_MODEL_ENUM.PAYE_FTH,
            ],
      ...(isBureau ? { includeInPayeCalculation: true, isBureau: true } : {}),
    };
    dispatch(paymentSettingActions.getPaymentRateList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const refetchData = useCallback(
    () => handleFetchData({ ...queryParams }),
    [queryParams],
  );

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['paymentSetting.paymentRate.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          onClick={toggleCreatePaymentRate}
          leftIcon={<PlusOutlined />}
        />
      </div>
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const toggleViewPaymentRate = () =>
    setOpenViewPaymentRate((prev) => ({
      ...prev,
      open: !prev.open,
    }));
  const handleViewPaymentRate = (data: PaymentRate) =>
    setOpenViewPaymentRate({
      open: true,
      data,
    });

  const toggleCreatePaymentRate = () =>
    setOpenCreatePaymentRate((prev) => ({
      ...prev,
      open: !prev.open,
    }));

  const isFiltering = !!queryParams?.search;

  const IRELAND_HIDDEN_RATE_CODES = ['PRTRONC'];
  const filteredList = isIreland
    ? (list ?? []).filter(
        (item) => !IRELAND_HIDDEN_RATE_CODES.includes(item.code),
      )
    : (list ?? []);
  const filteredTotal = isIreland
    ? Math.max(0, pagination.totalDocs - ((list?.length ?? 0) - filteredList.length))
    : pagination.totalDocs;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={filteredList}
            loading={loading}
            page={pagination.current}
            total={filteredTotal}
            pageSize={pagination.pageSize}
            scroll={{ x: isIreland ? 3100 : isUK ? 2300 : 2600, y: 700 }}
            titleEmpty={messages['paymentSetting.payment.empty']}
            columns={getColumns({
              messages,
              paymentSettingType: PaymentSettingType.PAYMENT_RATE,
              handleViewPaymentRate,
              isBureau,
              isIreland,
              isUK,
            })}
            onChangePagination={onChangePagination}
            noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
            addNewBtnOnClick={toggleCreatePaymentRate}
            dataTypeName={messages['common.paymentRate']}
          />
        </AppsContent>
      </AppCard>
      {openViewPaymentRate.open && (
        <ViewPaymentRateModal
          isOpen={openViewPaymentRate.open}
          toggle={toggleViewPaymentRate}
          paymentRateId={openViewPaymentRate.data?.id}
          refetchData={refetchData}
        />
      )}
      {openCreatePaymentRate.open && (
        <CreatePaymentRateModal
          isOpen={openCreatePaymentRate.open}
          toggle={toggleCreatePaymentRate}
          refetchData={refetchData}
        />
      )}
    </AppsContainer>
  );
};

export default PaymentRateListing;
